import { useTranslation } from 'react-i18next';

/**
 * Tastatur -- was auf diesem Rechner welche Taste ist.
 *
 * Eigene Datei und nicht im Bauteil ``Kbd``: Ein Modul, das eine Komponente
 * und daneben Konstanten ausfuehrt, laesst sich beim Entwickeln nicht mehr
 * einzeln neu laden.
 */

/**
 * Ob dieser Rechner die Befehlstaste hat.
 *
 * Das ist keine Kosmetik: Wer auf einem Windows-Rechner "⌘K" liest, sucht
 * eine Taste, die es dort nicht gibt. Ermittelt wird es einmal beim Laden --
 * ``navigator.platform`` gilt als veraltet, ``userAgent`` ist die Angabe, die
 * jeder Browser noch liefert. Im Zweifel gilt "nein": Die Steuerungstaste ist
 * die Angabe, die auf mehr Geraeten richtig ist.
 */
export const HAS_COMMAND_KEY: boolean =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.userAgent);

/**
 * Die Beschriftung der Zusatztaste, in der Sprache der Oberflaeche. Sie steht
 * in den Sprachdateien und nicht hier: "Strg" heisst auf Englisch "Ctrl".
 */
export function useModifierLabel(): string {
  const { t } = useTranslation();
  return HAS_COMMAND_KEY ? t('shortcuts.modifierCommand') : t('shortcuts.modifierControl');
}

/**
 * Ob ein Tastendruck die Zusatztaste traegt.
 *
 * Beide Tasten werden geprueft und nicht nur die des erkannten Systems: Eine
 * externe Tastatur an einem Mac schickt haeufig ``ctrlKey``, und ein Kuerzel,
 * das dann nichts tut, gilt als kaputt.
 */
export function hasModifier(event: KeyboardEvent): boolean {
  return event.metaKey || event.ctrlKey;
}

/**
 * Ob ein Tastendruck in einem Eingabefeld passiert ist.
 *
 * Einzelne Buchstaben als Kuerzel sind bequem und gefaehrlich: Wer "n" fuer
 * "neues Ticket" belegt, hat es im Betreff eines Tickets belegt. Kuerzel ohne
 * Zusatztaste fragen deshalb zuerst hier nach.
 */
export function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}
